import { Injectable } from '@angular/core';
import { Load, LoadWithView } from '../models/load.model';

export interface LoadValidationError {
  field: 'carrierOfRecord' | 'bookedFrom' | 'temperature' | 'stops' | 'stopDates' | 'load';
  message: string;
}

export interface LoadValidationResult {
  isValid: boolean;
  errors: LoadValidationError[];
  firstErrorMessage?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LoadValidationService {
  /**
   * Validates whether a load meets all requirements to transition from DRAFT to VERIFIED.
   * Requirements:
   * 1. Carrier of Record must be fully populated (company ID + company name).
   * 2. Booked From counterparty must be linked to a valid directory partner (company ID + company name).
   *    Raw text strings from AI parsing without an entity ID fail validation.
   * 3. Reefer loads require a valid temperature setpoint.
   * 4. Stops require at least 1 pickup and 1 delivery, with valid scheduled dates.
   */
  validateDraftLoad(load: LoadWithView | Load | null | undefined): LoadValidationResult {
    const errors: LoadValidationError[] = [];

    if (!load) {
      return {
        isValid: false,
        errors: [{ field: 'load', message: 'No load data available' }],
        firstErrorMessage: 'No load data available',
      };
    }

    const view = (load as LoadWithView).view;

    // 1. Carrier of Record Validation
    const carrierId = load.carrierOfRecordCompanyId || load.carrierId;
    const carrierName = load.carrierOfRecordCompanyName || load.carrierName;
    if (!carrierId || !carrierName || carrierId.trim() === '' || carrierName.trim() === '') {
      errors.push({
        field: 'carrierOfRecord',
        message: 'Carrier of Record must be selected with a valid company from the directory.',
      });
    }

    // 2. Booked From (Counterparty) Validation
    // Check if counterparty has a valid entity ID and name (not just unlinked raw parser text)
    let bookedFromId: string | null | undefined = null;
    let bookedFromName: string | null | undefined = null;

    if (view) {
      if (view.bookedFromRole === 'broker' || view.brokerId || view.brokerName) {
        bookedFromId = view.bookedFromCompanyId || view.brokerId;
        bookedFromName = view.bookedFromCompanyName || view.brokerName;
      } else if (view.bookedFromRole === 'customer' || view.customerId || view.customerName) {
        bookedFromId = view.bookedFromCompanyId || view.customerId;
        bookedFromName = view.bookedFromCompanyName || view.customerName;
      } else if (view.bookedFromRole === 'carrier') {
        bookedFromId = view.bookedFromCompanyId || load.carrierOfRecordCompanyId || load.carrierId;
        bookedFromName = view.bookedFromCompanyName || load.carrierOfRecordCompanyName || load.carrierName;
      } else {
        bookedFromId = view.bookedFromCompanyId;
        bookedFromName = view.bookedFromCompanyName;
      }
    } else {
      // Fallback check on parent doc if view isn't attached
      if (load.originalBrokerCompanyId || load.originalBrokerCompanyName) {
        bookedFromId = load.originalBrokerCompanyId;
        bookedFromName = load.originalBrokerCompanyName;
      }
    }

    if (!bookedFromId || bookedFromId.trim() === '' || !bookedFromName || bookedFromName.trim() === '') {
      errors.push({
        field: 'bookedFrom',
        message: 'Booking counterparty must be linked to a partner in your directory (ID required).',
      });
    }

    // 3. Reefer Temperature Validation
    const trailerType = (load.trailerType || '').toUpperCase();
    const isReefer = trailerType.includes('REEFER');
    if (isReefer) {
      const temp = load.temperature;
      const hasValidTemp =
        temp != null &&
        (temp.value != null ||
          temp.min != null ||
          (temp as any).setpoint != null ||
          (temp.max != null && !isNaN(temp.max)));
      if (!hasValidTemp) {
        errors.push({
          field: 'temperature',
          message: 'Reefer loads require a valid temperature setpoint.',
        });
      }
    }

    // 4. Stops Validation (>= 1 pickup, >= 1 delivery, scheduled dates)
    const stops = load.stops || [];
    if (!Array.isArray(stops) || stops.length < 2) {
      errors.push({
        field: 'stops',
        message: 'Load must contain at least 2 stops (1 pickup and 1 delivery).',
      });
    } else {
      const hasPickup = stops.some((s) => s.stopType === 'pickup' || s.stopType === 'both');
      const hasDelivery = stops.some((s) => s.stopType === 'delivery' || s.stopType === 'both');

      if (!hasPickup) {
        errors.push({
          field: 'stops',
          message: 'Load must contain at least one pickup stop.',
        });
      }

      if (!hasDelivery) {
        errors.push({
          field: 'stops',
          message: 'Load must contain at least one delivery stop.',
        });
      }

      // Check dates on mandatory stops
      const missingDates = stops
        .filter((s) => s.stopType === 'pickup' || s.stopType === 'delivery' || s.stopType === 'both')
        .some((s) => {
          const hasApptDate = !!(s.appointment?.from?.date || s.appointment?.to?.date);
          const hasEtaDate = !!(s.etaDateTime?.date || s.arrivalDateTime?.date);
          return !hasApptDate && !hasEtaDate;
        });

      if (missingDates) {
        errors.push({
          field: 'stopDates',
          message: 'All pickup and delivery stops must have a scheduled appointment date.',
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      firstErrorMessage: errors.length > 0 ? errors[0].message : undefined,
    };
  }
}
